import React, { useEffect, useState, useRef, useContext } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Col, Container, Row, Table, Button, Input } from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import { examMasterApi } from "apis/ExamMasterApi"
import { sessionApi } from "apis/SessionApi"
import { studentMarkEntries } from "apis/MarkEntriesApi"
import { Link } from "react-router-dom"
import { SessionContext } from "context/sessionContext"

const selectStyles = {
  menu: provided => ({ ...provided, backgroundColor: "#fff", zIndex: 1000 }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#fff",
    maxHeight: "200px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#f8f9fa" : "#fff",
    color: "#000",
    cursor: "pointer",
  }),
}

const GRADE_OPTIONS = [
  { value: "A+", label: "A+" },
  { value: "A", label: "A" },
  { value: "B+", label: "B+" },
  { value: "B", label: "B" },
  { value: "C+", label: "C+" },
  { value: "C", label: "C" },
  { value: "D+", label: "D+" },
  { value: "D", label: "D" },
  { value: "AB", label: "AB" },
]

const ATTEMPT_OPTIONS = [
  { value: 1, label: "Attempt 1" },
  { value: 2, label: "Attempt 2" },
  { value: 3, label: "Attempt 3" },
  { value: 4, label: "Attempt 4" },
]

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns assessment options for a given exam.
 * If an assessment has sub_assessments, return those; otherwise return the assessment itself.
 */
const buildAssessmentOptions = exam => {
  if (!exam) return []
  const options = []
  ;(exam.assessments || []).forEach(a => {
    if (a.sub_assessments && a.sub_assessments.length > 0) {
      a.sub_assessments.forEach(sa => {
        options.push({
          value: sa.id,
          label: `${a.assessment_name} › ${sa.sub_assessment_name}`,
          parentAssessmentId: a.id,
          isSubAssessment: true,
          subAssessmentData: sa,
          assessmentData: a,
        })
      })
    } else {
      options.push({
        value: a.id,
        label: a.assessment_name,
        parentAssessmentId: null,
        isSubAssessment: false,
        subAssessmentData: null,
        assessmentData: a,
      })
    }
  })
  return options
}

/**
 * Build category options from a selected assessment option.
 */
const buildCategoryOptionsFromAssessment = assessmentOption => {
  if (!assessmentOption) return []
  const seen = new Map()
  const subjects = assessmentOption.isSubAssessment
    ? assessmentOption.subAssessmentData?.subjects || []
    : assessmentOption.assessmentData?.subjects || []

  subjects.forEach(s => {
    if (!seen.has(s.category_id)) {
      seen.set(s.category_id, {
        value: s.category_id,
        label: s.category_name,
      })
    }
  })
  return Array.from(seen.values())
}

/**
 * Build class options from a selected assessment + category.
 */
const buildClassOptionsFromAssessment = (assessmentOption, categoryId) => {
  if (!assessmentOption || !categoryId) return []
  const subjects = assessmentOption.isSubAssessment
    ? assessmentOption.subAssessmentData?.subjects || []
    : assessmentOption.assessmentData?.subjects || []

  const seen = new Set()
  subjects.forEach(s => {
    if (s.category_id === categoryId) {
      const classes = Array.isArray(s.st_class) ? s.st_class : []
      classes.forEach(cls => {
        const trimmed = String(cls).trim()
        if (trimmed) seen.add(trimmed)
      })
    }
  })

  return Array.from(seen).map(cls => {
    const parts = cls.split(" ")
    const st_class = parts[0] || cls
    const st_sec = parts.slice(1).join(" ") || ""
    return { value: cls, label: cls.toUpperCase(), st_class, st_sec }
  })
}

/**
 * Build subject options from selected assessment + category.
 */
const buildSubjectOptions = (assessmentOption, categoryId) => {
  if (!assessmentOption || !categoryId) return []
  const subjects = assessmentOption.isSubAssessment
    ? assessmentOption.subAssessmentData?.subjects || []
    : assessmentOption.assessmentData?.subjects || []

  return subjects
    .filter(s => s.category_id === categoryId)
    .map(s => ({
      value: s.id,
      label: s.topic_name
        ? `${s.subject_name} — ${s.topic_name}`
        : s.subject_name,
      subjectData: s,
    }))
}



// ── Component ─────────────────────────────────────────────────────────────────

const MarkEntries = () => {
  document.title = "Mark Entries"

  // ── Filter state ──────────────────────────────────────────────────────
  const [selectedExam, setSelectedExam] = useState(null)
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedClass, setSelectedClass] = useState(null)
  const [selectedAttempt, setSelectedAttempt] = useState(null)
  const [selectedSubject, setSelectedSubject] = useState(null)

  // ── Options ───────────────────────────────────────────────────────────
  const [examOptions, setExamOptions] = useState([])
  const [assessmentOptions, setAssessmentOptions] = useState([])
  const [categoryOptions, setCategoryOptions] = useState([])
  const [classList, setClassList] = useState([])
  const [subjectOptions, setSubjectOptions] = useState([])
  const [examsData, setExamsData] = useState([])

  // ── Session ───────────────────────────────────────────────────────────
  const { Session } = useContext(SessionContext)
  const [fallbackSessionId, setFallbackSessionId] = useState(null)
  const activeSessionId = Session || sessionStorage.getItem("SessionId") || fallbackSessionId

  // ── Subject min/max marks ─────────────────────────────────────────────
  const [subjectMinMark, setSubjectMinMark] = useState("")
  const [subjectMaxMark, setSubjectMaxMark] = useState("")
  const [originalMinMark, setOriginalMinMark] = useState("")
  const [originalMaxMark, setOriginalMaxMark] = useState("")
  const [evaluationType, setEvaluationType] = useState(null) // "Marks Based" | "Grade Based"

  // ── Table state ───────────────────────────────────────────────────────
  const [students, setStudents] = useState([])
  const [studentEntries, setStudentEntries] = useState({}) // { [st_code]: mark | grade }
  const [originalEntries, setOriginalEntries] = useState({}) // snapshot of last fetched entries
  const [examName, setExamName] = useState("")
  const [categoryName, setCategoryName] = useState("")
  const [hasGenerated, setHasGenerated] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // ── Loading flags ─────────────────────────────────────────────────────
  const [isUpdatingMarks, setIsUpdatingMarks] = useState(false)
  const [isSavingEntry, setIsSavingEntry] = useState(false)

  const [examsPage, setExamsPage] = useState(1)
  const [examsTotalPages, setExamsTotalPages] = useState(1)
  const [examsLoadingMore, setExamsLoadingMore] = useState(false)

  const [searchQuery, setSearchQuery] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")

  // ── Mark-entries pagination ───────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // ── Cross-page entry tracking ─────────────────────────────────────────
  // allServerEntries: ALL entries fetched from the server for the current
  //   subject (across every page). Populated once per subject selection.
  // pendingEdits: any marks the user typed that haven't been saved yet,
  //   accumulated across pages so they survive navigation.
  const allServerEntries = useRef({}) // { [st_code]: savedValue }
  const pendingEdits = useRef({})     // { [st_code]: typedValue }
  const nextAttemptEntries = useRef({}) // { [st_code]: nextAttemptSavedValue }

  // ── Derived flags ─────────────────────────────────────────────────────
  const isMarksChanged =
    subjectMinMark !== originalMinMark || subjectMaxMark !== originalMaxMark
  const hasAnyEntry = Object.values(studentEntries).some(
    v => v !== "" && v !== null && v !== undefined
  )

  const isDirty = students.some(student => {
    const key = student.st_code
    const current = studentEntries[key] ?? ""
    const original = originalEntries[key] ?? ""
    return current !== original
  })
  const canSaveEntry = hasAnyEntry || isDirty
  const isMarksBased = evaluationType === "Marks Based"

  // ── Fetch fallback active session silently if none is set in context/storage ──
  useEffect(() => {
    const fetchSession = async () => {
      if (Session || sessionStorage.getItem("SessionId")) return
      try {
        const res = await sessionApi.GetSession()
        if (res?.data?.session && Array.isArray(res.data.session)) {
          const active = res.data.session.find(s => s.is_active)
          if (active) setFallbackSessionId(active.id)
          else if (res.data.session.length > 0)
            setFallbackSessionId(res.data.session[0].id)
        }
      } catch (err) {
        console.error("Failed to fetch sessions:", err.message)
      }
    }
    fetchSession()
  }, [Session])

  // ── Fetch exams ───────────────────────────────────────────────────────
  const fetchExams = async (page = 1) => {
    try {
      if (page > 1) setExamsLoadingMore(true)

      const response = await examMasterApi.getExam(page, "", activeSessionId ? parseInt(activeSessionId, 10) : null)
      const allExams = response?.data?.exams || []
      const exams = allExams
      const totalPages = response?.data?.total_pages ?? 1

      setExamsData(prev => (page === 1 ? exams : [...prev, ...exams]))
      setExamOptions(prev =>
        page === 1
          ? exams.map(e => ({ value: e.id, label: e.exam_name }))
          : [...prev, ...exams.map(e => ({ value: e.id, label: e.exam_name }))]
      )
      setExamsTotalPages(totalPages)
      setExamsPage(page)
    } catch {
      Swal.fire("Error", "Failed to fetch exams.", "error")
    } finally {
      setExamsLoadingMore(false)
    }
  }

  useEffect(() => {
    if (activeSessionId) {
      setSelectedExam(null)
      setSelectedAssessment(null)
      setSelectedCategory(null)
      setSelectedClass(null)
      setSelectedAttempt(null)
      setSelectedSubject(null)
      setExamOptions([])
      setAssessmentOptions([])
      setCategoryOptions([])
      setClassList([])
      setSubjectOptions([])
      resetTableState()
      fetchExams(1)
    }
  }, [activeSessionId])

  const handleExamMenuScrollToBottom = () => {
    if (!examsLoadingMore && examsPage < examsTotalPages) {
      fetchExams(examsPage + 1)
    }
  }

  // ── Reset helpers ─────────────────────────────────────────────────────
  const resetTableState = () => {
    setHasGenerated(false)
    setStudents([])
    setStudentEntries({})
    setOriginalEntries({})
    setExamName("")
    setCategoryName("")
    setSearchQuery("")
    setAppliedSearch("")
    setCurrentPage(1)
    setTotalPages(1)
    setTotalCount(0)
    allServerEntries.current = {}
    pendingEdits.current = {}
    nextAttemptEntries.current = {}
  }

  const resetSubjectState = () => {
    setSelectedSubject(null)
    setSubjectMinMark("")
    setSubjectMaxMark("")
    setOriginalMinMark("")
    setOriginalMaxMark("")
    setEvaluationType(null)
  }

  // ── Cascade handlers ──────────────────────────────────────────────────
  const handleExamChange = option => {
    setSelectedExam(option)
    setSelectedAssessment(null)
    setSelectedCategory(null)
    setSelectedClass(null)
    setSelectedAttempt(null)
    setAssessmentOptions([])
    setCategoryOptions([])
    setClassList([])
    setSubjectOptions([])
    resetSubjectState()
    resetTableState()

    if (option) {
      const matched = examsData.find(e => e.id === option.value)
      setAssessmentOptions(buildAssessmentOptions(matched))
    }
  }

  const handleAssessmentChange = option => {
    setSelectedAssessment(option)
    setSelectedCategory(null)
    setSelectedClass(null)
    setSelectedAttempt(null)
    setCategoryOptions([])
    setClassList([])
    setSubjectOptions([])
    resetSubjectState()
    resetTableState()

    if (option) {
      setCategoryOptions(buildCategoryOptionsFromAssessment(option))
    }
  }

  const handleCategoryChange = option => {
    setSelectedCategory(option)
    setSelectedClass(null)
    setSelectedAttempt(null)
    setClassList([])
    setSubjectOptions([])
    resetSubjectState()
    resetTableState()

    if (option && selectedAssessment) {
      setClassList(
        buildClassOptionsFromAssessment(selectedAssessment, option.value)
      )
      setSubjectOptions(buildSubjectOptions(selectedAssessment, option.value))
    }
  }

  const handleClassChange = option => {
    setSelectedClass(option)
    setSelectedAttempt(null)
    resetTableState()
  }

  const handleAttemptChange = option => {
    setSelectedAttempt(option)
    resetTableState()
  }

  const handleSubjectChange = option => {
    setSelectedSubject(option)
    // Clear cross-page tracking whenever subject changes
    allServerEntries.current = {}
    pendingEdits.current = {}
    nextAttemptEntries.current = {}
    if (option) {
      const s = option.subjectData
      setEvaluationType(s.evaluation_type)
      if (s.evaluation_type === "Marks Based") {
        const mn = s.min_mark != null ? String(s.min_mark) : ""
        const mx = s.max_mark != null ? String(s.max_mark) : ""
        setSubjectMinMark(mn)
        setSubjectMaxMark(mx)
        setOriginalMinMark(mn)
        setOriginalMaxMark(mx)
      } else {
        setSubjectMinMark("")
        setSubjectMaxMark("")
        setOriginalMinMark("")
        setOriginalMaxMark("")
      }
      // If students are already loaded, fetch existing marks for this subject
      if (hasGenerated && students.length > 0) {
        setTimeout(
          () => fetchAndApplyExistingMarksForOption(option, students),
          0
        )
      }
    } else {
      setEvaluationType(null)
      setSubjectMinMark("")
      setSubjectMaxMark("")
      setOriginalMinMark("")
      setOriginalMaxMark("")
      setStudentEntries({})
      setOriginalEntries({})
    }
  }

  // ── Shared helper: fetch ALL pages of mark entries for a given payload ──────
  // Returns a { [st_code]: valueString } map populated from every page.
  const fetchAllMarkEntries = async basePayload => {
    const serverMap = {}
    let page = 1
    let totalPagesRemote = 1

    do {
      const res = await studentMarkEntries.getMarkEntries({ ...basePayload, page })
      if (res?.data?.status === 200 && Array.isArray(res.data.entries)) {
        totalPagesRemote = res.data.total_pages ?? 1
        res.data.entries.forEach(entry => {
          // mark_value of 0 is valid — only skip if null/undefined
          // "AB" from server is displayed as "-" in marks-based inputs
          let val =
            entry.mark_value !== null && entry.mark_value !== undefined
              ? String(entry.mark_value)
              : entry.grade_value !== null && entry.grade_value !== undefined
              ? String(entry.grade_value)
              : null
          if (val === "AB" && entry.mark_value !== null && entry.mark_value !== undefined) val = "-"
          if (val !== null) serverMap[entry.st_code] = val
        })
        page++
      } else {
        break // stop on error
      }
    } while (page <= totalPagesRemote)

    return serverMap
  }

  // Variant of fetchAndApplyExistingMarks that accepts option+students directly
  // (avoids stale closure issue when called right after setState)
  const fetchAndApplyExistingMarksForOption = async (
    subjectOption,
    currentStudents
  ) => {
    if (
      !activeSessionId ||
      !selectedExam ||
      !selectedAssessment ||
      !selectedCategory ||
      !selectedClass ||
      !subjectOption
    )
      return

    try {
      const s = subjectOption.subjectData
      const basePayload = {
        session_id: activeSessionId,
        exam_id: selectedExam.value,
        assessment_id: selectedAssessment.isSubAssessment
          ? selectedAssessment.parentAssessmentId
          : selectedAssessment.value,
        ...(selectedAssessment.isSubAssessment && {
          sub_assessment_id: selectedAssessment.value,
        }),
        category_id: selectedCategory.value,
        st_class: selectedClass.st_class,
        st_sec: selectedClass.st_sec,
        subject_code: s.subject_code,
        category_subject_id: subjectOption.value,
        attempt: selectedAttempt.value,
      }

      const serverMap = await fetchAllMarkEntries(basePayload)
      allServerEntries.current = serverMap

      let nextServerMap = {}
      if (selectedAttempt && selectedAttempt.value) {
        try {
          nextServerMap = await fetchAllMarkEntries({
            ...basePayload,
            attempt: selectedAttempt.value + 1,
          })
        } catch (e) {
          console.error("Failed to fetch next attempt entries:", e)
        }
      }
      nextAttemptEntries.current = nextServerMap

      // Merge: server values + any pending edits, scoped to current page
      const merged = { ...serverMap, ...pendingEdits.current }
      const pageMap = {}
      currentStudents.forEach(st => {
        if (Object.prototype.hasOwnProperty.call(merged, st.st_code))
          pageMap[st.st_code] = merged[st.st_code]
      })
      setStudentEntries(pageMap)
      setOriginalEntries({ ...serverMap })
    } catch (err) {
      console.error("Failed to fetch existing mark entries:", err.message)
      allServerEntries.current = {}
      setStudentEntries({})
      setOriginalEntries({})
    }
  }

  // ── Fetch existing mark entries and apply to inputs ───────────────────
  const fetchAndApplyExistingMarks = async currentStudents => {
    if (
      !activeSessionId ||
      !selectedExam ||
      !selectedAssessment ||
      !selectedCategory ||
      !selectedClass ||
      !selectedSubject
    )
      return

    try {
      const s = selectedSubject.subjectData
      const basePayload = {
        session_id: activeSessionId,
        exam_id: selectedExam.value,
        assessment_id: selectedAssessment.isSubAssessment
          ? selectedAssessment.parentAssessmentId
          : selectedAssessment.value,
        ...(selectedAssessment.isSubAssessment && {
          sub_assessment_id: selectedAssessment.value,
        }),
        category_id: selectedCategory.value,
        st_class: selectedClass.st_class,
        st_sec: selectedClass.st_sec,
        subject_code: s.subject_code,
        category_subject_id: selectedSubject.value,
        attempt: selectedAttempt.value,
      }

      const serverMap = await fetchAllMarkEntries(basePayload)
      allServerEntries.current = serverMap

      let nextServerMap = {}
      if (selectedAttempt && selectedAttempt.value) {
        try {
          nextServerMap = await fetchAllMarkEntries({
            ...basePayload,
            attempt: selectedAttempt.value + 1,
          })
        } catch (e) {
          console.error("Failed to fetch next attempt entries:", e)
        }
      }
      nextAttemptEntries.current = nextServerMap

      // Merge: server values + any pending edits, scoped to current page
      const merged = { ...serverMap, ...pendingEdits.current }
      const pageMap = {}
      currentStudents.forEach(st => {
        if (Object.prototype.hasOwnProperty.call(merged, st.st_code))
          pageMap[st.st_code] = merged[st.st_code]
      })
      setStudentEntries(pageMap)
      setOriginalEntries({ ...serverMap })
    } catch (err) {
      console.error("Failed to fetch existing mark entries:", err.message)
      allServerEntries.current = {}
      setStudentEntries({})
      setOriginalEntries({})
    }
  }

  // ── Fetch students ────────────────────────────────────────────────────
  const fetchStudents = async (page = 1, search = "") => {
    if (
      !activeSessionId ||
      !selectedExam ||
      !selectedCategory ||
      !selectedClass ||
      !selectedAttempt
    )
      return

    setIsLoading(true)
    try {
      const payload = {
        session_id: activeSessionId,
        exam_id: selectedExam.value,
        category_id: selectedCategory.value,
        st_class: selectedClass.st_class,
        st_sec: selectedClass.st_sec,
        attempt: selectedAttempt.value,
        page,
        search,
        ...(selectedAssessment && {
          assessment_id: selectedAssessment.isSubAssessment
            ? selectedAssessment.parentAssessmentId
            : selectedAssessment.value,
          ...(selectedAssessment.isSubAssessment && {
            sub_assessment_id: selectedAssessment.value,
          }),
        }),
        ...(selectedSubject && {
          subject_code: selectedSubject.subjectData?.subject_code,
          topic_name: selectedSubject.subjectData?.topic_name || "",
        }),
      }

      const res = await studentMarkEntries.getMarkEntryStudents(payload)

      if (res?.data?.status === 200 && Array.isArray(res.data.students)) {
        const fetchedStudents = res.data.students
        setStudents(fetchedStudents)
        setExamName(res.data.exam_name || "")
        setCategoryName(res.data.category_name || "")
        setTotalPages(res.data.total_pages ?? 1)
        setTotalCount(res.data.total_count ?? fetchedStudents.length)
        setCurrentPage(res.data.page_number ?? page)
        setHasGenerated(true)

        if (selectedSubject && selectedAssessment) {
          // If we already have server entries cached (cross-page navigation),
          // restore from cache — no need to hit the server again.
          if (Object.keys(allServerEntries.current).length > 0) {
            const merged = { ...allServerEntries.current, ...pendingEdits.current }
            const pageMap = {}
            fetchedStudents.forEach(st => {
              if (Object.prototype.hasOwnProperty.call(merged, st.st_code))
                pageMap[st.st_code] = merged[st.st_code]
            })
            setStudentEntries(pageMap)
            setOriginalEntries({ ...allServerEntries.current })
          } else {
            // First load for this subject — fetch all pages from server
            await fetchAndApplyExistingMarks(fetchedStudents)
          }
        } else {
          setStudentEntries({})
        }
      } else {
        Swal.fire(
          "Error",
          res?.data?.msg || "Failed to fetch students.",
          "error"
        )
      }
    } catch (err) {
      console.error("Failed to fetch mark entry students:", err.message)
      Swal.fire("Error", "Failed to fetch students. Please try again.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  // ── Update subject marks config ───────────────────────────────────────
  const handleUpdateMarks = async () => {
    if (
      !selectedSubject ||
      !selectedAssessment ||
      !selectedCategory ||
      !selectedClass
    )
      return

    const s = selectedSubject.subjectData
    const payload = {
      assessment_id: selectedAssessment.isSubAssessment
        ? selectedAssessment.parentAssessmentId
        : selectedAssessment.value,
      ...(selectedAssessment.isSubAssessment && {
        sub_assessment_id: selectedAssessment.value,
      }),
      category_id: selectedCategory.value,
      st_class: selectedClass.st_class,
      subject_code: s.subject_code,
      topic_name: s.topic_name || "",
      min_mark: Number(subjectMinMark),
      max_mark: Number(subjectMaxMark),
    }

    setIsUpdatingMarks(true)
    try {
      const res = await studentMarkEntries.updateSubjectMarksConfig(payload)
      const data = res?.data
      if (data?.status === 200) {
        Swal.fire(
          "Success",
          data.msg || "Marks updated successfully.",
          "success"
        )
        setOriginalMinMark(subjectMinMark)
        setOriginalMaxMark(subjectMaxMark)
      } else {
        Swal.fire("Error", data?.msg || "Failed to update marks.", "error")
      }
    } catch (err) {
      console.error("Failed to update marks config:", err.message)
      Swal.fire("Error", "Failed to update marks. Please try again.", "error")
    } finally {
      setIsUpdatingMarks(false)
    }
  }

  // ── Save mark entries ─────────────────────────────────────────────────
  const handleSaveMarkEntry = async () => {
  if (
    !selectedSubject ||
    !selectedAssessment ||
    !selectedCategory ||
    !selectedClass
  ) return

  // ── Client-side min/max validation ───────────────────────────────────
  if (isMarksBased) {
    const min = subjectMinMark !== "" ? Number(subjectMinMark) : null
    const max = subjectMaxMark !== "" ? Number(subjectMaxMark) : null
    const violations = []

    students.forEach(student => {
      const val = studentEntries[student.st_code] ?? ""
      if (val === "" || val === null || val === undefined) return
      // Skip validation for absent ("-") entries
      if (val === "-") return

      const num = Number(val)
      if (min !== null && num < min) {
        violations.push(
          `<li><strong>${student.st_code}</strong> (${student.st_name}): Below min mark (${min})</li>`
        )
      } else if (max !== null && num > max) {
        violations.push(
          `<li><strong>${student.st_code}</strong> (${student.st_name}): Exceeds max mark (${max})</li>`
        )
      }
    })

    if (violations.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Failed",
        html: `
          <p style="margin-bottom:10px">
            ${violations.length} entry/entries have invalid marks.
          </p>
          <ul style="text-align:center;margin:0;padding-left:18px">
            ${violations.join("")}
          </ul>`,
      })
      return
    }
  }

    const s = selectedSubject.subjectData

    // Build a complete merged map: server-saved entries + any pending edits
    // (including what the user has typed on the current page via studentEntries)
    const fullMerged = {
      ...allServerEntries.current,
      ...pendingEdits.current,
      ...studentEntries, // current page's live state wins
    }

    // Collect all known student codes from the merged map + current page
    const allCodes = new Set([
      ...students.map(st => st.st_code),
      ...Object.keys(fullMerged),
    ])

    const entries = []
    allCodes.forEach(stCode => {
      if (isStudentAttemptLocked(stCode)) return
      const val = fullMerged[stCode] ?? ""
      const originalVal = originalEntries[stCode] ?? allServerEntries.current[stCode] ?? ""
      // Include if there's a value, OR if a previously saved value was cleared
      const hasValue = val !== "" && val !== null && val !== undefined
      const wasCleared = originalVal !== "" && val === ""
      if (!hasValue && !wasCleared) return

      const isCleared = val === "" || val === null || val === undefined
      // Find student meta from current page if available, else use minimal data
      const studentMeta = students.find(st => st.st_code === stCode)
      // "-" in marks-based input means Absent → send mark_value as "AB"
      const isAbsent = isMarksBased && val === "-"
      entries.push({
        st_code: stCode,
        st_name: studentMeta?.st_name || "",
        slno: studentMeta?.roll_no ? String(studentMeta.roll_no) : "",
        mark_value: isMarksBased ? (isCleared ? null : isAbsent ? "AB" : Number(val)) : null,
        grade_value: !isMarksBased ? (isCleared ? "" : String(val)) : "",
      })
    })

    if (entries.length === 0) return

    const payload = {
      session_id: activeSessionId,
      assessment_id: selectedAssessment.isSubAssessment
        ? selectedAssessment.parentAssessmentId
        : selectedAssessment.value,
      ...(selectedAssessment.isSubAssessment && {
        sub_assessment_id: selectedAssessment.value,
      }),
      category_subject_id: selectedSubject.value, // ← FK to ExamCategorySubject
      st_class: selectedClass.st_class,
      st_sec: selectedClass.st_sec,
      attempt: selectedAttempt.value,
      entries,
    }

    setIsSavingEntry(true)
    try {
      const res = await studentMarkEntries.saveMarkEntry(payload)
      const data = res?.data
      if (data?.status === 200) {
        Swal.fire(
          "Success",
          data.msg || "Mark entries saved successfully.",
          "success"
        )
        // Refresh inputs with the latest saved values from the server
        await fetchAndApplyExistingMarksForOption(selectedSubject, students)
      } else {
        // Build detailed error message if the API returned per-entry errors
        const errors = Array.isArray(data?.errors) ? data.errors : []
        let html = `<p style="margin-bottom:${errors.length ? "10px" : "0"}">${
          data?.msg || "Failed to save mark entries."
        }</p>`
        if (errors.length > 0) {
          html += `<ul style="text-align:center;margin:0;padding-left:18px">`
          errors.forEach(e => {
            html += `<li><strong>${e.st_code}</strong>: ${e.error}</li>`
          })
          html += `</ul>`
        }
        Swal.fire({
          icon: "error",
          title: "Save Failed",
          html,
        })
      }
    } catch (err) {
      console.error("Failed to save mark entries:", err.message)
      Swal.fire(
        "Error",
        "Failed to save mark entries. Please try again.",
        "error"
      )
    } finally {
      setIsSavingEntry(false)
    }
  }

  // ── Validate & generate ───────────────────────────────────────────────
  const handleGenerate = () => {
    if (!selectedExam)
      return Swal.fire("Validation", "Please select an Examination.", "warning")
    if (!selectedAssessment)
      return Swal.fire("Validation", "Please select an Assessment.", "warning")
    if (!selectedCategory)
      return Swal.fire("Validation", "Please select a Category.", "warning")
    if (!selectedClass)
      return Swal.fire(
        "Validation",
        "Please select a Class / Section.",
        "warning"
      )
    if (!selectedAttempt)
      return Swal.fire("Validation", "Please select an Attempt.", "warning")
    setSearchQuery("")
    setAppliedSearch("")
    fetchStudents(1, "")
  }

  // ── Reset ─────────────────────────────────────────────────────────────
  const handleReset = () => {
    setSelectedExam(null)
    setSelectedAssessment(null)
    setSelectedCategory(null)
    setSelectedClass(null)
    setSelectedAttempt(null)
    setAssessmentOptions([])
    setCategoryOptions([])
    setClassList([])
    setSubjectOptions([])
    resetSubjectState()
    resetTableState()
  }

  useEffect(() => {
    if (!hasGenerated) return
    const delayDebounceFn = setTimeout(() => {
      setAppliedSearch(searchQuery)
      fetchStudents(1, searchQuery)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery, hasGenerated])

  // ── Search & pagination for students table ────────────────────────────
  const handleSearch = () => {
    if (!hasGenerated) return
    setAppliedSearch(searchQuery)
    fetchStudents(1, searchQuery)
  }

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") handleSearch()
  }

  const handlePageChange = page => {
    if (page < 1 || page > totalPages) return
    // pendingEdits is already kept in sync by handleEntryChange on every keystroke,
    // so just fetch the new page — entries will be restored from the cache.
    fetchStudents(page, appliedSearch)
  }

  // ── Pagination renderer ───────────────────────────────────────────────
  const renderPagination = () => {
    if (!hasGenerated || totalPages <= 1) return null
    const maxVisible = 5
    const pages = []
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          {totalCount > 0 && (
            <>&nbsp;|&nbsp;Total: <strong>{totalCount}</strong> students</>
          )}
        </small>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(1)}>«</button>
          </li>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>‹</button>
          </li>
          {pages.map(p => (
            <li key={p} className={`page-item ${p === currentPage ? "active" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(p)}>{p}</button>
            </li>
          ))}
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>›</button>
          </li>
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(totalPages)}>»</button>
          </li>
        </ul>
      </div>
    )
  }

  // ── Student entry change ────────────────────────────────────────────
  const handleEntryChange = (stCode, value) => {
    // Update display state for the current page
    setStudentEntries(prev => ({ ...prev, [stCode]: value }))
    // Persist the edit so it survives page navigation
    if (value !== "" && value !== null && value !== undefined) {
      pendingEdits.current[stCode] = value
    } else {
      // If cleared, track as explicit empty so it overrides any server value
      pendingEdits.current[stCode] = ""
    }
  }

  const isStudentAttemptLocked = stCode => {
    if (!selectedAttempt || !selectedAttempt.value) return false
    const nextAttemptVal = nextAttemptEntries.current[stCode]
    const hasNextAttemptMarks =
      nextAttemptVal !== undefined && nextAttemptVal !== null && nextAttemptVal !== ""

    if (!hasNextAttemptMarks) return false

    const currentVal = allServerEntries.current[stCode] ?? ""

    if (isMarksBased) {
      const minMark = subjectMinMark !== "" ? Number(subjectMinMark) : null
      if (minMark === null) return false

      const isAbsent = currentVal === "-" || currentVal === "AB"
      const score = Number(currentVal)
      const isFailed = isAbsent || (!isNaN(score) && score < minMark)
      return isFailed
    } else {
      // Grade-based
      const isFailed =
        currentVal === "E" ||
        currentVal === "D" ||
        currentVal === "AB" ||
        currentVal === "-" ||
        currentVal === ""
      return isFailed
    }
  }

  // ── Table body ────────────────────────────────────────────────────────
  const renderTableBody = () => {
    if (!hasGenerated && !isLoading) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-user-circle"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Select all filters and click <strong>Get Students</strong> to view
            the list.
          </td>
        </tr>
      )
    }

    if (isLoading) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-loader-alt bx-spin"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Loading students...
          </td>
        </tr>
      )
    }

    if (students.length === 0) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-search-alt"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            No students found for the selected filters.
          </td>
        </tr>
      )
    }

    return students.map((student, index) => {
      const key = student.st_code || index
      const entryValue = studentEntries[key] ?? ""

      // "-" means Absent — skip numeric validation for AB entries
      const isAbsentEntry = entryValue === "-"
      const valNum = (!isAbsentEntry && entryValue !== "" && entryValue !== null && entryValue !== undefined) ? Number(entryValue) : NaN
      const minVal = (subjectMinMark !== "" && subjectMinMark !== null && subjectMinMark !== undefined) ? Number(subjectMinMark) : null
      const maxVal = (subjectMaxMark !== "" && subjectMaxMark !== null && subjectMaxMark !== undefined) ? Number(subjectMaxMark) : null

      const isBelowMin = !isAbsentEntry && !isNaN(valNum) && minVal !== null && valNum < minVal
      const isAboveMax = !isAbsentEntry && !isNaN(valNum) && maxVal !== null && valNum > maxVal
      const isInvalid = isBelowMin || isAboveMax

      return (
        <tr key={key} className="text-center">
          <td>{student.roll_no}</td>
          <td>{student.st_code}</td>
          <td className="text-start">{student.st_name}</td>
          <td style={{ minWidth: "160px" }}>
            {isMarksBased ? (
              <div className="d-flex flex-column align-items-center">
                <Input
                  type="text"
                  bsSize="sm"
                  placeholder="Marks"
                  value={entryValue}
                  disabled={isStudentAttemptLocked(key)}
                  onChange={e => {
                    const v = e.target.value
                    // Allow digits, decimal point, or "-" (absent)
                    if (v === "" || v === "-" || /^\d*\.?\d*$/.test(v)) {
                      handleEntryChange(key, v)
                    }
                  }}
                  style={{
                    width: "120px",
                    textAlign: "center",
                    backgroundColor: isAbsentEntry
                      ? "#fff3e0"
                      : isInvalid
                      ? "#fde8e8"
                      : entryValue !== ""
                      ? "#f0fff4"
                      : undefined,
                    borderColor: isAbsentEntry
                      ? "#ff9800"
                      : isInvalid
                      ? "#dc3545"
                      : entryValue !== ""
                      ? "#28a745"
                      : undefined,
                  }}
                />
                {isAbsentEntry && (
                  <span
                    className="mt-1"
                    style={{ fontSize: "11px", fontWeight: "500", color: "#e65100" }}
                  >
                    AB (Absent)
                  </span>
                )}
                {isBelowMin && (
                  <span
                    className="text-danger mt-1"
                    style={{ fontSize: "11px", fontWeight: "500" }}
                  >
                    {"< " + minVal}
                  </span>
                )}
                {isAboveMax && (
                  <span
                    className="text-danger mt-1"
                    style={{ fontSize: "11px", fontWeight: "500" }}
                  >
                    {"> " + maxVal}
                  </span>
                )}
              </div>
            ) : evaluationType === "Grade Based" ? (
              <Select
                classNamePrefix="select2-selection"
                placeholder="Grade"
                options={GRADE_OPTIONS}
                isDisabled={isStudentAttemptLocked(key)}
                value={
                  entryValue
                    ? GRADE_OPTIONS.find(g => g.value === entryValue) || null
                    : null
                }
                onChange={opt => handleEntryChange(key, opt ? opt.value : "")}
                styles={{
                  ...selectStyles,
                  container: provided => ({ ...provided, width: "130px" }),
                  control: provided => ({
                    ...provided,
                    ...(entryValue !== ""
                      ? { backgroundColor: "#f0fff4", borderColor: "#28a745" }
                      : {}),
                  }),
                }}
                menuPosition="fixed"
              />
            ) : (
              <span className="text-muted" style={{ fontSize: "12px" }}>
                Select a subject
              </span>
            )}
          </td>
        </tr>
      )
    })
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid={true}>
            <Breadcrumb title="Examination" breadcrumbItem="Mark Entries" />

            {/* ── Filter Section ──────────────────────────────────────── */}
            <div className="p-3 mb-4 rounded bg-white">
              {/* Row 1: Exam → Assessment → Category → Class → Attempt */}
              <Row className="gx-4">
                {/* Examination */}
                <Col lg={3} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Examination <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_exam"
                    classNamePrefix="select2-selection"
                    placeholder="Select Examination..."
                    options={examOptions}
                    value={selectedExam}
                    onChange={handleExamChange}
                    onMenuScrollToBottom={handleExamMenuScrollToBottom}
                    isLoading={examsLoadingMore}
                    styles={selectStyles}
                  />
                </Col>

                {/* Assessment / Sub-Assessment */}
                <Col lg={3} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Assessment <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_assessment"
                    classNamePrefix="select2-selection"
                    placeholder={
                      !selectedExam
                        ? "Select Exam first..."
                        : assessmentOptions.length === 0
                        ? "No assessments available"
                        : "Select Assessment..."
                    }
                    options={assessmentOptions}
                    value={selectedAssessment}
                    onChange={handleAssessmentChange}
                    isDisabled={!selectedExam || assessmentOptions.length === 0}
                    styles={selectStyles}
                  />
                </Col>

                {/* Category */}
                <Col lg={2} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Category <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_category"
                    classNamePrefix="select2-selection"
                    placeholder={
                      !selectedAssessment
                        ? "Select..."
                        : "Select Category..."
                    }
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    isDisabled={!selectedAssessment}
                    styles={selectStyles}
                  />
                </Col>

                {/* Class / Section */}
                <Col lg={2} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Class / Section <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_class"
                    classNamePrefix="select2-selection"
                    placeholder={
                      !selectedCategory
                        ? "Select..."
                        : classList.length === 0
                        ? "No classes available"
                        : "Select Class / Section..."
                    }
                    options={classList}
                    value={selectedClass}
                    onChange={handleClassChange}
                    isDisabled={!selectedCategory || classList.length === 0}
                    styles={selectStyles}
                  />
                </Col>

                {/* Attempt */}
                <Col lg={2} md={6} className="mb-3" style={{ maxWidth: "150px" }}>
                  <label className="form-label fw-semibold mb-1">
                    Attempt <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="attempt"
                    classNamePrefix="select2-selection"
                    placeholder={
                      !selectedClass
                        ? "Select..."
                        : "Select Attempt..."
                    }
                    options={ATTEMPT_OPTIONS}
                    value={selectedAttempt}
                    onChange={handleAttemptChange}
                    isDisabled={!selectedClass}
                    styles={selectStyles}
                  />
                </Col>
              </Row>

              {/* Row 2: Subject + min/max marks (shown after class and attempt are selected) */}
              {selectedClass && selectedAttempt && (
                <Row className="align-items-end">
                  {/* Subject */}
                  <Col lg={4} md={6} className="mb-3">
                    <label className="form-label fw-semibold mb-1">
                      Subject
                    </label>
                    <Select
                      name="st_subject"
                      classNamePrefix="select2-selection"
                      placeholder={
                        subjectOptions.length === 0
                          ? "No subjects available"
                          : "Select Subject..."
                      }
                      options={subjectOptions}
                      value={selectedSubject}
                      onChange={handleSubjectChange}
                      isDisabled={subjectOptions.length === 0}
                      styles={selectStyles}
                      isClearable
                    />
                  </Col>

                  {/* Min / Max marks — only for Marks Based */}
                  {selectedSubject && isMarksBased && (
                    <>
                      <Col lg={2} md={3} className="mb-3">
                        <label className="form-label fw-semibold mb-1">
                          Min Marks
                        </label>
                        <Input
                          type="number"
                          value={subjectMinMark}
                          onChange={e => setSubjectMinMark(e.target.value)}
                          placeholder="Min"
                        />
                      </Col>
                      <Col lg={2} md={3} className="mb-3">
                        <label className="form-label fw-semibold mb-1">
                          Max Marks
                        </label>
                        <Input
                          type="number"
                          value={subjectMaxMark}
                          onChange={e => setSubjectMaxMark(e.target.value)}
                          placeholder="Max"
                        />
                      </Col>
                      <Col className="mb-3 d-flex align-items-end">
                        <Button
                          color="warning"
                          className="me-2"
                          disabled={!isMarksChanged || isUpdatingMarks}
                          onClick={handleUpdateMarks}
                        >
                          <i className="bx bx-edit me-1" />
                          {isUpdatingMarks
                            ? "Updating..."
                            : "Update Subject Marks"}
                        </Button>
                      </Col>
                    </>
                  )}
                </Row>
              )}

              {/* Action buttons */}
              <Row>
                <Col>
                  <Button
                    color="primary"
                    onClick={handleGenerate}
                    className="me-2"
                    disabled={isLoading}
                  >
                    <i className="bx bx-user-check me-1" />
                    {isLoading ? "Loading..." : "Get Students"}
                  </Button>

                  <Button
                    color="secondary"
                    outline
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    <i className="bx bx-reset me-1" />
                    Reset
                  </Button>
                </Col>
              </Row>
            </div>

            {/* ── Filter Summary ──────────────────────────────────────── */}
            {hasGenerated && (
              <div
                className="p-3 mb-3 rounded d-flex flex-wrap gap-3"
                style={{
                  backgroundColor: "#e8f4fd",
                  border: "1px solid #b8daff",
                  fontSize: "13px",
                }}
              >
                <span>
                  <strong>Examination:</strong>{" "}
                  <span className="text-primary">
                    {examName || selectedExam?.label}
                  </span>
                </span>
                <span className="text-muted">|</span>
                <span>
                  <strong>Assessment:</strong>{" "}
                  <span className="text-primary">
                    {selectedAssessment?.label}
                  </span>
                </span>
                <span className="text-muted">|</span>
                <span>
                  <strong>Category:</strong>{" "}
                  <span className="text-primary">
                    {categoryName || selectedCategory?.label}
                  </span>
                </span>
                <span className="text-muted">|</span>
                <span>
                  <strong>Class:</strong>{" "}
                  <span className="text-primary">{selectedClass?.label}</span>
                </span>
                {selectedSubject && (
                  <>
                    <span className="text-muted">|</span>
                    <span>
                      <strong>Subject:</strong>{" "}
                      <span className="text-primary">{selectedSubject.label}</span>
                    </span>
                  </>
                )}
                {selectedAttempt && (
                  <>
                    <span className="text-muted">|</span>
                    <span>
                      <strong>Attempt:</strong>{" "}
                      <span className="text-primary">{selectedAttempt.label}</span>
                    </span>
                  </>
                )}
                <span className="text-muted">|</span>
                <span>
                  <strong>Total Students:</strong>{" "}
                  <span className="text-primary">{totalCount > 0 ? totalCount : students.length}</span>
                </span>
              </div>
            )}

            {/* ── Top action row ──────────────────────────────────────── */}
            <Row className="mb-3 align-items-center">
              <Col lg={5} md={12} className="mb-2 mb-lg-0">
                <div className="d-flex gap-2">
                  <div style={{ position: "relative", width: "100%" }}>
                    <Input
                      type="text"
                      placeholder="Search by student name or code..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      disabled={!hasGenerated}
                      style={{ fontSize: "13px", paddingRight: "30px" }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery("")
                          setAppliedSearch("")
                          fetchStudents(1, "")
                        }}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          fontSize: "12px",
                          padding: 0,
                          color: "#6c757d",
                          zIndex: 5,
                        }}
                      >
                        ❌
                      </button>
                    )}
                  </div>
                  <Button
                    color="primary"
                    onClick={handleSearch}
                    disabled={!hasGenerated || isLoading}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <i className="bx bx-search me-1" />
                    Search
                  </Button>
                </div>
              </Col>
              <Col lg={7} md={12}>
                <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
                   <Link to="/report">
                     <Button color="primary" outline>
                       <i className="bx bx-bar-chart-alt-2 me-1" />
                       <strong>View Report</strong>
                     </Button>
                   </Link>
                  <Button
                    color="success"
                    disabled={!canSaveEntry || isSavingEntry}
                    onClick={handleSaveMarkEntry}
                  >
                    <i className="bx bx-save me-1" />
                    {isSavingEntry ? "Saving..." : "Submit Mark"}
                  </Button>
                </div>
              </Col>
            </Row>

            {/* ── Students Table ──────────────────────────────────────── */}
            <div className="table-responsive">
              <Table className="table table-striped table-bordered align-middle">
                <thead>
                  <tr className="text-center table-secondary">
                    <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                      Roll No
                    </th>
                    <th style={{ width: "140px", whiteSpace: "nowrap" }}>
                      Student Code
                    </th>
                    <th className="text-start" style={{ minWidth: "180px" }}>
                      Student Name
                    </th>
                    <th
                      className="text-start"
                      style={{ minWidth: "160px", whiteSpace: "nowrap" }}
                    >
                      Mark Entry
                    </th>
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </Table>
            </div>
            {renderPagination()}
          </Container>
        </div>
      </div>
    </React.Fragment>
  )
}

export default MarkEntries
